/**
 * How a reply failed, shown where it failed.
 *
 * A turn that dies mid-flight used to report only above the composer. That put
 * the failure at the bottom of the screen, unattached to anything: with three
 * tool rows in the reply the reader could not tell which one broke, and a thread
 * reopened later showed the steps with no sign that anything had gone wrong at
 * all. It is the same defect as `inspect_revit_with_code` logging every
 * rejection as "transport failure" — a failure reported away from its cause
 * costs whoever reads it next the work of guessing.
 *
 * `bimgo-ai-web` puts it in the flow as an `error` event, and this is that card:
 * its geometry and colours (`Message.tsx`'s `ErrorSection` — red-50 on red-200,
 * an alert triangle, red-800 text at `text-sm`).
 *
 * The one addition is the trace reference underneath, which the reference has no
 * equivalent for. It was already on 2.5's composer card and is what support uses
 * to find the turn in the logs, so moving the error into the flow had to bring it
 * along rather than trade it away.
 */
import type { ChatError } from "../../hooks/useChatTurn";
import { TraceReference } from "./TraceReference";

/** lucide `AlertTriangle` — the reference's icon, at its 16px. */
function AlertTriangleIcon() {
  return (
    <svg
      className="msg-error-icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

export function MessageError({ error }: { error: ChatError | undefined }) {
  // An error with no sentence in it is not worth a card — the reference returns
  // nothing for an empty message, and an empty red box says only "something".
  if (!error || !error.text) return null;

  return (
    // `role="alert"` so the failure is announced rather than only drawn. It is
    // the same role the composer card carries, for the same reason.
    <div className="msg-error" role="alert">
      <AlertTriangleIcon />
      <div className="msg-error-body">
        <p className="msg-error-text">{error.text}</p>
        {error.traceId ? <TraceReference traceId={error.traceId} className="msg-error-ref" /> : null}
      </div>
    </div>
  );
}
